import React, { useState } from "react";
import {
  CheckCircle,
  Mail,
  Brain,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Target,
  ArrowRight,
  Heart,
  Compass,
  Shield,
  Lightbulb,
  Map,
  Calendar,
  Users,
  Play,
  Clock,
} from "lucide-react";

const SuccessScreen = ({ firstName, email, fullReport, onStartNew }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  // Function to convert URLs to clickable links
  const makeLinksClickable = (str) => {
    if (typeof str !== "string") return str;
    const urlRegex = /(https?:\/\/[^\s)]+)/g;
    const parts = str.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        const cleanUrl = part.replace(/[.,;:!?)]+$/, "");
        const punctuation = part.slice(cleanUrl.length);

        return (
          <React.Fragment key={index}>
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-adhd-yellow hover:text-adhd-orange underline font-medium transition-colors break-all"
            >
              {cleanUrl}
            </a>
            {punctuation}
          </React.Fragment>
        );
      }
      return part;
    });
  };

  // Parse inline formatting (bold, italics, links)
  const parseInlineFormatting = (text) => {
    if (!text || typeof text !== "string") return text;

    let result = [];
    let remaining = text;
    let keyCounter = 0;

    while (remaining.length > 0) {
      // Check for markdown links [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)/);
      if (linkMatch) {
        result.push(
          <a
            key={keyCounter++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-adhd-yellow hover:text-adhd-orange underline font-medium transition-colors"
          >
            {linkMatch[1]}
          </a>
        );
        remaining = remaining.slice(linkMatch[0].length);
        continue;
      }

      // Check for bold (**text**)
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        result.push(
          <strong key={keyCounter++} className="font-bold text-adhd-yellow">
            {makeLinksClickable(boldMatch[1])}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Check for italic (*text*) - but not ** or *emoji
      const italicMatch = remaining.match(/^\*([^*\s][^*]*[^*\s])\*/);
      if (italicMatch && !remaining.startsWith("**")) {
        result.push(
          <em key={keyCounter++} className="italic text-gray-200">
            {makeLinksClickable(italicMatch[1])}
          </em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // Check for emoji patterns like 🎯, 💛, etc. at start of text
      const emojiMatch = remaining.match(
        /^([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|✓|✅|→)/u
      );
      if (emojiMatch) {
        result.push(
          <span key={keyCounter++} className="mr-1">
            {emojiMatch[1]}
          </span>
        );
        remaining = remaining.slice(emojiMatch[0].length);
        continue;
      }

      // Check for raw URLs (not markdown links)
      const rawUrlMatch = remaining.match(/^(https?:\/\/[^\s)]+)/);
      if (rawUrlMatch) {
        const cleanUrl = rawUrlMatch[1].replace(/[.,;:!?)]+$/, "");
        const after = rawUrlMatch[1].slice(cleanUrl.length);
        result.push(
          <a
            key={keyCounter++}
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-adhd-yellow hover:text-adhd-orange underline font-medium transition-colors break-all"
          >
            {cleanUrl}
          </a>
        );
        if (after) result.push(after);
        remaining = remaining.slice(rawUrlMatch[0].length);
        continue;
      }

      const nextSpecial = remaining.search(/\[|\*|https?:\/\//);
      if (nextSpecial === -1) {
        // No more special characters, add the rest
        result.push(...makeLinksClickable(remaining));
        break;
      } else if (nextSpecial === 0) {
        // Special char at start but didn't match patterns above, skip it
        result.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        // Add text up to the special character
        result.push(...makeLinksClickable(remaining.slice(0, nextSpecial)));
        remaining = remaining.slice(nextSpecial);
      }
    }

    return result.length > 0 ? result : text;
  };

  // Enhanced content parser
  const parseReportContent = (text) => {
    if (!text) return null;

    const lines = text.split("\n");
    const elements = [];
    let currentList = [];
    let currentListType = null;
    let paragraphBuffer = [];
    let keyCounter = 0;

    const flushParagraph = () => {
      if (paragraphBuffer.length > 0) {
        const content = paragraphBuffer.join(" ").trim();
        if (content) {
          elements.push(
            <p
              key={`p-${keyCounter++}`}
              className="text-gray-300 leading-relaxed mb-4"
            >
              {parseInlineFormatting(content)}
            </p>
          );
        }
        paragraphBuffer = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        if (currentListType === "numbered") {
          elements.push(
            <div key={`list-${keyCounter++}`} className="space-y-3 my-4">
              {currentList.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border border-adhd-yellow/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-adhd-yellow">
                      {item.number}
                    </span>
                  </div>
                  <p className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </p>
                </div>
              ))}
            </div>
          );
        } else if (currentListType === "bullet") {
          elements.push(
            <ul key={`list-${keyCounter++}`} className="space-y-2 my-4">
              {currentList.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-adhd-yellow mt-1.5 text-xs">●</span>
                  <span className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        } else if (currentListType === "checklist") {
          elements.push(
            <ul key={`list-${keyCounter++}`} className="space-y-2 my-4">
              {currentList.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-adhd-green mt-0.5">✓</span>
                  <span className="text-gray-300 leading-relaxed flex-1">
                    {parseInlineFormatting(item.text)}
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        currentList = [];
        currentListType = null;
      }
    };

    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();

      // Skip empty lines but flush current content
      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }

      // Handle horizontal rules (---, ___, ***)
      if (/^[-_*]{3,}$/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        elements.push(
          <hr
            key={`hr-${keyCounter++}`}
            className="border-adhd-yellow/20 my-6"
          />
        );
        return;
      }

      // Handle === style headers
      if (/^={3,}$/.test(trimmedLine)) {
        flushParagraph();
        flushList();
        elements.push(
          <hr
            key={`hr-${keyCounter++}`}
            className="border-adhd-yellow/30 my-8 border-2"
          />
        );
        return;
      }

      // Handle markdown headers
      if (trimmedLine.startsWith("####")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^####\s*/, "").trim();
        elements.push(
          <h5
            key={`h4-${keyCounter++}`}
            className="text-base font-bold text-gray-200 mt-5 mb-2"
          >
            {parseInlineFormatting(headerText)}
          </h5>
        );
        return;
      }

      if (trimmedLine.startsWith("###")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^###\s*/, "").trim();
        elements.push(
          <h4
            key={`h3-${keyCounter++}`}
            className="text-lg font-bold text-gray-100 mt-6 mb-3 flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 bg-adhd-yellow rounded-full"></span>
            {parseInlineFormatting(headerText)}
          </h4>
        );
        return;
      }

      if (trimmedLine.startsWith("##")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^##\s*/, "").trim();
        elements.push(
          <h3
            key={`h2-${keyCounter++}`}
            className="text-xl font-bold text-adhd-yellow mt-8 mb-4 pb-2 border-b border-adhd-yellow/20"
          >
            {parseInlineFormatting(headerText)}
          </h3>
        );
        return;
      }

      if (trimmedLine.startsWith("#") && !trimmedLine.startsWith("##")) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.replace(/^#\s*/, "").trim();
        elements.push(
          <h2
            key={`h1-${keyCounter++}`}
            className="text-2xl md:text-3xl font-black gradient-text mt-8 mb-6"
          >
            {parseInlineFormatting(headerText)}
          </h2>
        );
        return;
      }

      // Handle standalone bold headers (like **The Surface Problem**)
      if (
        trimmedLine.startsWith("**") &&
        trimmedLine.endsWith("**") &&
        trimmedLine.indexOf("**", 2) === trimmedLine.length - 2
      ) {
        flushParagraph();
        flushList();
        const headerText = trimmedLine.slice(2, -2).trim();
        elements.push(
          <h4
            key={`boldheader-${keyCounter++}`}
            className="text-lg font-bold text-adhd-yellow mt-6 mb-3"
          >
            {headerText}
          </h4>
        );
        return;
      }

      // Handle checklist items (✓ or ✅)
      if (trimmedLine.startsWith("✓") || trimmedLine.startsWith("✅")) {
        flushParagraph();
        if (currentListType !== "checklist") {
          flushList();
          currentListType = "checklist";
        }
        const itemText = trimmedLine.replace(/^[✓✅]\s*/, "").trim();
        if (itemText) {
          currentList.push({ text: itemText });
        }
        return;
      }

      // Handle numbered lists
      const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
      if (numberedMatch) {
        flushParagraph();
        if (currentListType !== "numbered") {
          flushList();
          currentListType = "numbered";
        }
        currentList.push({
          number: numberedMatch[1],
          text: numberedMatch[2].trim(),
        });
        return;
      }

      // Handle bullet points (various formats)
      const bulletMatch = trimmedLine.match(/^[•\-*●]\s+(.+)/);
      if (bulletMatch) {
        flushParagraph();
        if (currentListType !== "bullet") {
          flushList();
          currentListType = "bullet";
        }
        currentList.push({ text: bulletMatch[1].trim() });
        return;
      }

      // Handle blockquote style (lines starting with > or ")
      if (trimmedLine.startsWith(">") || trimmedLine.startsWith('"')) {
        flushParagraph();
        flushList();
        const quoteText = trimmedLine
          .replace(/^[>"]\s*/, "")
          .replace(/"$/, "")
          .trim();
        elements.push(
          <blockquote
            key={`quote-${keyCounter++}`}
            className="border-l-4 border-adhd-yellow/50 pl-4 py-2 my-4 bg-adhd-yellow/5 rounded-r-lg"
          >
            <p className="text-gray-200 italic">
              {parseInlineFormatting(quoteText)}
            </p>
          </blockquote>
        );
        return;
      }

      // Add to paragraph buffer
      paragraphBuffer.push(trimmedLine);
    });

    // Flush any remaining content
    flushParagraph();
    flushList();

    return elements;
  };

  // Parse the report into main sections
  const parseReport = (reportContent) => {
    if (!reportContent) return [];

    const sections = [];
    const lines = reportContent.split("\n");
    let currentSection = null;
    let currentContent = [];

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      // Check for main section headers (## SECTION or **SECTION**)
      const h2Match = trimmedLine.match(/^##\s+(.+)/);
      const boldHeaderMatch =
        trimmedLine.match(/^\*\*([A-Z][^*]+)\*\*$/) ||
        trimmedLine.match(/^\*\*([A-Z]{2,}[^*]+)\*\*$/);

      if (h2Match || boldHeaderMatch) {
        // Save previous section if exists
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join("\n").trim(),
          });
        }
        // Start new section
        currentSection = h2Match
          ? h2Match[1].trim()
          : boldHeaderMatch[1].trim();
        currentContent = [];
      } else {
        currentContent.push(line);
      }
    });

    // Add last section
    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join("\n").trim(),
      });
    }

    // If no sections were found with the above method, try alternative parsing
    if (sections.length === 0) {
      // Try to split by PART headers
      const partRegex = /^(PART\s+\d+:?\s*.+)$/gm;
      let match;
      const partMatches = [];

      while ((match = partRegex.exec(reportContent)) !== null) {
        partMatches.push({ title: match[1], index: match.index });
      }

      if (partMatches.length > 0) {
        partMatches.forEach((part, idx) => {
          const endIndex =
            idx < partMatches.length - 1
              ? partMatches[idx + 1].index
              : reportContent.length;

          sections.push({
            title: part.title,
            content: reportContent
              .slice(part.index + part.title.length, endIndex)
              .trim(),
          });
        });
      } else {
        // Fallback: return whole content as single section
        sections.push({
          title: "Your Transformation Report",
          content: reportContent,
        });
      }
    }

    return sections;
  };

  // Get appropriate icon for section
  const getSectionIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("mirror") || lowerTitle.includes("truth"))
      return Brain;
    if (lowerTitle.includes("architecture") || lowerTitle.includes("struggle"))
      return Shield;
    if (lowerTitle.includes("identity") || lowerTitle.includes("archaeology"))
      return Heart;
    if (lowerTitle.includes("belief") || lowerTitle.includes("code"))
      return Lightbulb;
    if (lowerTitle.includes("harmony") || lowerTitle.includes("diagnostic"))
      return Target;
    if (lowerTitle.includes("ikigai") || lowerTitle.includes("revealed"))
      return Compass;
    if (lowerTitle.includes("blueprint") || lowerTitle.includes("week"))
      return Map;
    if (lowerTitle.includes("implementation") || lowerTitle.includes("path"))
      return ArrowRight;
    if (lowerTitle.includes("program") || lowerTitle.includes("reinvent"))
      return Calendar;
    if (lowerTitle.includes("webinar") || lowerTitle.includes("invitation"))
      return Users;
    if (lowerTitle.includes("note") || lowerTitle.includes("personal"))
      return Heart;
    if (lowerTitle.includes("part 1")) return Brain;
    if (lowerTitle.includes("part 2")) return Heart;
    if (lowerTitle.includes("part 3")) return Lightbulb;
    if (lowerTitle.includes("part 4")) return Target;
    if (lowerTitle.includes("part 5")) return Compass;
    return Sparkles;
  };

  const reportSections = fullReport
    ? parseReport(fullReport.reportContent)
    : [];

  return (
    <div className="min-h-screen gradient-bg py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 animate-fadeIn mb-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-adhd-green/20 to-adhd-green/10 border border-adhd-green/30 rounded-full flex items-center justify-center animate-scaleIn">
              <CheckCircle className="w-10 h-10 text-adhd-green" />
            </div>

            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="gradient-text">Well done, {firstName}!</span>
            </h1>

            <p className="text-xl text-gray-300 mb-8">
              Your ADHD Transformation Report is ready.
            </p>
          </div>

          {/* Email Notification */}
          <div className="glass-light rounded-2xl p-6 border border-adhd-yellow/10">
            <div className="flex items-start gap-4">
              <div className="p-3 glass rounded-xl">
                <Mail className="w-6 h-6 text-adhd-yellow" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-100 mb-2">
                  We've also sent this report to your email
                </h3>
                <p className="text-gray-300 mb-2">Check your inbox at:</p>
                <p className="font-bold text-adhd-yellow bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border border-adhd-yellow/30 px-4 py-2 rounded-lg inline-block">
                  {email}
                </p>
                <p className="text-sm text-gray-400 mt-3">
                  Please check your spam folder if you don't see it within 15
                  minutes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pre-Report Webinar CTA */}
        <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 animate-slideUp mb-8 border-2 border-adhd-yellow/30 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-adhd-yellow/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-adhd-orange/10 to-transparent rounded-full blur-2xl translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-adhd-yellow/50"></div>
              <span className="text-adhd-yellow text-sm font-bold uppercase tracking-wider">Before You Read Your Report</span>
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-adhd-yellow/50"></div>
            </div>

            <h2 className="text-2xl md:text-3xl font-black text-center mb-4">
              <span className="text-gray-100">🎬 Watch This </span>
              <span className="gradient-text">First</span>
            </h2>

            <p className="text-lg text-gray-300 text-center max-w-2xl mx-auto mb-6">
              Your report is packed with valuable insights about <strong className="text-adhd-yellow">YOUR</strong> unique ADHD patterns. 
              But before you dive in, there's something important you need to understand first.
            </p>

            <div className="glass-light rounded-2xl p-6 border border-adhd-yellow/20 mb-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 flex-shrink-0 bg-gradient-to-br from-adhd-yellow/20 to-adhd-orange/20 border-2 border-adhd-yellow/40 rounded-2xl flex items-center justify-center">
                  <Play className="w-10 h-10 text-adhd-yellow" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    The Day 6 Live Webinar
                  </h3>
                  <p className="text-gray-300">
                    In this webinar, Jim will walk you through the <strong className="text-adhd-yellow">complete framework</strong> that 
                    makes sense of everything in your report. You'll understand <em>why</em> these patterns exist and exactly 
                    <strong className="text-adhd-yellow"> how to transform them</strong> into your greatest strengths.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="glass-light rounded-xl p-4 border border-adhd-yellow/10 text-center">
                <Brain className="w-8 h-8 text-adhd-yellow mx-auto mb-2" />
                <p className="text-sm text-gray-300">The psychology behind your patterns</p>
              </div>
              <div className="glass-light rounded-xl p-4 border border-adhd-yellow/10 text-center">
                <Target className="w-8 h-8 text-adhd-yellow mx-auto mb-2" />
                <p className="text-sm text-gray-300">The 6-week transformation system</p>
              </div>
              <div className="glass-light rounded-xl p-4 border border-adhd-yellow/10 text-center">
                <Compass className="w-8 h-8 text-adhd-yellow mx-auto mb-2" />
                <p className="text-sm text-gray-300">Your path to ADHD-aligned success</p>
              </div>
            </div>

            <div className="text-center">
              <a
                href="https://www.skool.com/adhd/classroom/4521a501"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-10 py-5 premium-button text-black rounded-xl transition-all font-bold text-lg hover-lift shadow-xl shadow-adhd-yellow/20"
              >
                <Calendar size={22} />
                <span>Add to Your Calendar</span>
                <ArrowRight size={22} />
              </a>
              <p className="text-gray-400 text-sm mt-4 flex items-center justify-center gap-2">
                <Clock size={14} />
                <span>Free to attend - don't miss it</span>
              </p>
            </div>
          </div>
        </div>

        {/* Full Report Display */}
        {fullReport && (
          <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 animate-slideUp">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border border-adhd-yellow/30 rounded-full text-adhd-yellow text-sm font-semibold mb-4">
                <Sparkles size={16} />
                <span>Your Personal ADHD Transformation Report</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-100 mb-4">
                Your Journey to ADHD-Aligned Success
              </h2>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Based on your responses, we've created your personalized
                transformation blueprint - a psychological portrait built from
                YOUR words, YOUR patterns, YOUR psychology.
              </p>
            </div>

            {/* Report Sections */}
            <div className="space-y-4">
              {reportSections.map((section, index) => {
                const isExpanded = expandedSections[section.title] !== false;
                const SectionIcon = getSectionIcon(section.title);

                return (
                  <div
                    key={index}
                    className="glass-light rounded-2xl shadow-lg overflow-hidden border border-adhd-yellow/10 transition-all hover:border-adhd-yellow/20"
                  >
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="w-full px-6 py-4 bg-gradient-to-r from-adhd-yellow/5 to-adhd-orange/5 hover:from-adhd-yellow/10 hover:to-adhd-orange/10 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 glass border border-adhd-yellow/20 rounded-lg">
                          <SectionIcon size={20} className="text-adhd-yellow" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-100 text-left">
                          {section.title}
                        </h3>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={20} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="px-6 py-6 border-t border-adhd-yellow/10">
                        <div className="prose prose-invert max-w-none">
                          {parseReportContent(section.content)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Call to Action */}
            <div className="mt-12 p-8 md:p-10 bg-gradient-to-br from-adhd-yellow/15 to-adhd-orange/10 border-2 border-adhd-yellow/30 rounded-3xl relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-4 right-4 text-6xl opacity-20">🎯</div>
              <div className="absolute bottom-4 left-4 text-4xl opacity-10">✨</div>
              
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <span className="inline-block px-4 py-1 bg-adhd-yellow/20 border border-adhd-yellow/40 rounded-full text-adhd-yellow text-sm font-bold uppercase tracking-wider mb-4">
                    Don't Stop Here
                  </span>
                  <h3 className="text-2xl md:text-3xl font-black text-gray-100 mb-4">
                    🎯 Your Next Step: The Day 6 Live Webinar
                  </h3>
                </div>

                <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto text-center">
                  Now that you've read your report and understand your patterns, it's time to 
                  <strong className="text-adhd-yellow"> take action</strong>. Join Jim live as he reveals 
                  the complete 6-week transformation system and shows you exactly how to turn these insights 
                  into <strong className="text-adhd-yellow">lasting change</strong>.
                </p>

                <div className="glass-light rounded-xl p-5 mb-6 border border-adhd-yellow/20 max-w-xl mx-auto">
                  <p className="text-gray-200 text-center font-medium">
                    "The report shows you <em className="text-gray-400">what</em> is holding you back. 
                    The webinar shows you <em className="text-adhd-yellow">how</em> to break free."
                  </p>
                </div>

                <div className="text-center">
                  <a
                    href="https://www.skool.com/adhd/classroom/4521a501"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-10 py-5 premium-button text-black rounded-xl transition-all font-bold text-lg hover-lift shadow-xl shadow-adhd-yellow/20"
                  >
                    <Calendar size={22} />
                    <span>Add to Your Calendar</span>
                    <ArrowRight size={22} />
                  </a>
                  <p className="text-adhd-orange text-sm mt-4 font-semibold">
                    ⚠️ Seriously - you don't want to miss this.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400">
            Questions? Contact us at{" "}
            <a
              href="mailto:support@adhdharmony.com"
              className="text-adhd-yellow hover:text-adhd-orange transition-colors"
            >
              support@adhdharmony.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;
